import { memo, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  ModalBody,
  ModalContent,
  Modal,
  ModalHeader,
  ModalFooter,
  Tabs,
  Tab,
  Checkbox,
  RadioGroup,
  Radio,
} from '@heroui/react';
import { CalendarDate, Time, parseTime } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  type LessonFormat,
  type ModuleIntakeTerm,
  useCreateLessonMutation,
} from '@/entities/Courses';
import { useGetLocationsAllQuery } from '@/entities/Locations';
import { AppButton } from '@/shared/ui/AppButton';
import {
  CustomInput,
  CustomTextarea,
  CustomDatepicker,
  CustomTimeInput,
  CustomSelect,
  FieldHint,
} from '@/shared/ui/Fields';
import { Dropzone } from '@/shared/ui/Dropzone';
import { Divider } from '@/shared/ui/Divider';
import { Svg } from '@/shared/ui/Svg';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_HINT } from '@/shared/lib/constants/fileSizes';
import { combineDateAndTime, combineDateAndTimeAsUTC } from '@/shared/lib/utils/dateAdapters';
import { MessageModal } from '@/shared/ui/Modal/MessageModal';
import UploadVideoIcon from '@/shared/assets/icons/files/upload-video.svg';
import {
  SESSION_ATTACHMENT_ACCEPT,
  SESSION_ATTACHMENT_TYPES_HINT,
} from '../../../config/sessionAttachmentAccept';
import {
  testDeadlineWithinTerm,
  testSessionDateWithinTerm,
  testSlotEndWithinTerm,
  formatDeadlineHint,
  formatSessionDateHint,
  getSessionDatePickerProps,
  useSessionTermDates,
  type SessionDatesContext,
} from '../../../lib/sessionTermDates';
import { DurationField } from '../DurationField';
import {
  SessionStepsField,
  SESSION_STEP_LIMIT,
  describeBackgroundUploads,
  getSaveErrorMessage,
  useSaveSessionSteps,
  type SessionStepDraft,
} from '../SessionStepsField';
import styles from './AddSessionModal.module.scss';

interface AddSessionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeModuleId: number;
  groupId: number;
  // Session dates and deadlines must lie within it
  intakeTerm?: ModuleIntakeTerm | null;
  onSuccess?: () => void;
}

interface SessionFormData {
  duration: number;
  title: string;
  description: string;
  date: CalendarDate | null;
  startTime: Time | null;
  format: LessonFormat;
  locationId?: number;
  roomId?: number;
  conferenceLink: string;
  isOnlyVideo: boolean;
}

interface HomeworkFormData {
  duration: number;
  isExam: boolean;
  isResubmission: boolean;
  deadline: CalendarDate | null;
  numberOfAttempts: number;
  assignmentText: string;
  assignmentDescription: string;
}

const REQUIRED_MESSAGE = 'This field is required';


const sessionSchema = yup.object({
  title: yup.string().trim().required(REQUIRED_MESSAGE).max(255),
  description: yup.string().trim().required(REQUIRED_MESSAGE),
  date: yup.mixed().required(REQUIRED_MESSAGE).test('within-term', '', testSessionDateWithinTerm),
  startTime: yup
    .mixed()
    .required(REQUIRED_MESSAGE)
    .test('slot-end-within-term', '', testSlotEndWithinTerm),
  duration: yup.number().min(1, REQUIRED_MESSAGE).required(REQUIRED_MESSAGE),
  format: yup.mixed<LessonFormat>().oneOf(['online', 'offline', 'meeting']).required(),
  locationId: yup.number().when('format', {
    is: 'offline',
    then: (schema) => schema.required(REQUIRED_MESSAGE),
    otherwise: (schema) => schema.optional(),
  }),
  roomId: yup.number().when('format', {
    is: 'offline',
    then: (schema) => schema.required(REQUIRED_MESSAGE),
    otherwise: (schema) => schema.optional(),
  }),
  conferenceLink: yup.string().when('format', {
    is: 'meeting',
    then: (schema) => schema.trim().required(REQUIRED_MESSAGE),
    otherwise: (schema) => schema.optional(),
  }),
  isOnlyVideo: yup.boolean().optional(),
}) as yup.ObjectSchema<SessionFormData>;

const homeworkSchema = yup.object({
  isExam: yup.boolean(),
  isResubmission: yup.boolean(),
  deadline: yup.mixed().nullable().test('within-term', '', testDeadlineWithinTerm),
  numberOfAttempts: yup.number().min(1).optional(),
  assignmentText: yup.string().optional(),
  assignmentDescription: yup.string().optional(),
}) as yup.ObjectSchema<HomeworkFormData>;

const offlineSwitchText = (stepsCount: number) => {
  const removed = stepsCount > 2 ? `Steps 2-${stepsCount}` : 'Step 2';
  return `Offline sessions support only one step. If you switch, Step 1 will be kept and ${removed} will be permanently deleted. This action cannot be undone`;
};

export const AddSessionModal = memo(
  ({ isOpen, onOpenChange, intakeModuleId, groupId, intakeTerm, onSuccess }: AddSessionModalProps) => {
    const { toast } = useToast();
    const { termBounds, termLabel } = useSessionTermDates(intakeTerm);
    const [createLesson, { isLoading }] = useCreateLessonMutation();
    const { saveSteps, isSaving: isSavingSteps, status: stepsStatus } = useSaveSessionSteps();
    const [activeTab, setActiveTab] = useState<string>('session');
    const [sessionFiles, setSessionFiles] = useState<File[]>([]);
    const [steps, setSteps] = useState<SessionStepDraft[]>([]);
    const [offlineSwitchOpen, setOfflineSwitchOpen] = useState(false);
    // Keeps the created lesson so a retry after a failed step continues instead of duplicating it
    const [createdLessonId, setCreatedLessonId] = useState<number | null>(null);
    const [homeworkFiles, setHomeworkFiles] = useState<File[]>([]);
    const { data: locationsData } = useGetLocationsAllQuery();

    const {
      control: sessionControl,
      handleSubmit: handleSessionSubmit,
      formState: { errors: sessionErrors },
      reset: resetSession,
      trigger: triggerSession,
      clearErrors: clearSessionErrors,
      watch: watchSession,
      setValue: setSessionValue,
    } = useForm<SessionFormData, SessionDatesContext>({
      resolver: yupResolver(sessionSchema) as any,
      context: { termBounds, termLabel },
      mode: 'onTouched',
      defaultValues: {
        title: '',
        description: '',
        date: null,
        startTime: null,
        duration: 0,
        format: 'online',
        locationId: undefined,
        roomId: undefined,
        conferenceLink: '',
        isOnlyVideo: false,
      },
    });

    const watchedSessionDate = watchSession('date');
    const sessionDatePickerProps = getSessionDatePickerProps(termBounds);
    const deadlinePickerProps = getSessionDatePickerProps(termBounds, watchedSessionDate);
    const deadlineHint = formatDeadlineHint(termBounds, watchedSessionDate);
    const revalidateStartTime = () => {
      if (sessionErrors.startTime) void triggerSession('startTime');
    };

    const {
      control: homeworkControl,
      formState: { errors: homeworkErrors },
      reset: resetHomework,
      getValues: getHomeworkValues,
      trigger: triggerHomework,
    } = useForm<HomeworkFormData, SessionDatesContext>({
      resolver: yupResolver(homeworkSchema) as any,
      context: { termBounds, termLabel, sessionDate: watchedSessionDate },
      defaultValues: {
        isExam: false,
        isResubmission: false,
        deadline: null,
        numberOfAttempts: 1,
        assignmentText: '',
        assignmentDescription: '',
      },
    });

    const watchedSessionFormat = watchSession('format');
    const watchedLocationId = watchSession('locationId');
    const locations = locationsData?.data?.locations ?? [];
    const availableLocations = locations.filter((location) => !location.blocked);
    const locationItems = availableLocations.map((location) => ({
      key: String(location.id),
      label: location.title,
    }));
    const selectedLocation = availableLocations.find(
      (location) => location.id === watchedLocationId,
    );
    const roomItems =
      selectedLocation?.rooms
        .filter((room) => room.isAvailable)
        .map((room) => ({
          key: String(room.id),
          label: `${room.number} (${room.capacity})`,
        })) ?? [];

    const isBusy = isLoading || isSavingSteps;
    const saveStatus = isLoading ? 'Creating the session...' : stepsStatus;

    const handleClose = () => {
      if (isBusy) return;
      resetSession();
      resetHomework();
      setSessionFiles([]);
      setHomeworkFiles([]);
      setSteps([]);
      setCreatedLessonId(null);
      setOfflineSwitchOpen(false);
      setActiveTab('session');
      onOpenChange(false);
    };

    const markStep = (clientId: string, patch: Partial<SessionStepDraft>) => {
      setSteps((current) =>
        current.map((item) => (item.clientId === clientId ? { ...item, ...patch } : item)),
      );
    };

    const onSubmit = async (data: SessionFormData) => {
      let lessonId = createdLessonId;
      try {
        if (!data.date || !data.startTime) {
          toast({
            title: 'Error',
            description: 'Date and start time are required',
            color: 'danger',
          });
          return;
        }

        const scheduledAt = combineDateAndTimeAsUTC(data.date, data.startTime);

        if (!scheduledAt) {
          toast({
            title: 'Error',
            description: 'Invalid date or time',
            color: 'danger',
          });
          return;
        }
        const format: LessonFormat = data.format;
        if (format === 'meeting' && !data.conferenceLink.trim()) {
          toast({
            title: 'Error',
            description: 'Conference link is required for meeting',
            color: 'danger',
          });
          return;
        }
        if (format === 'offline' && !data.roomId) {
          toast({
            title: 'Error',
            description: 'Select location and room for offline session',
            color: 'danger',
          });
          return;
        }
        if (steps.length === 0) {
          toast({
            title: 'Error',
            description: 'A session must have at least one step',
            color: 'danger',
          });
          return;
        }


        if (!lessonId) {
          // The deadline lives on the Assignments tab: check it before creating the lesson
          if (!(await triggerHomework())) {
            setActiveTab('homework');
            return;
          }
          const homework = getHomeworkValues();
          const hasAssignment =
            !!homework.assignmentText.trim() ||
            !!homework.assignmentDescription.trim() ||
            homeworkFiles.length > 0;
          const endDate = homework.deadline
            ? combineDateAndTime(homework.deadline, parseTime('23:59'))
            : undefined;
          const isOnlyVideo = format === 'online' && Boolean(data.isOnlyVideo);

          const result = await createLesson({
            intakeModuleId,
            body: {
              title: data.title.trim(),
              description: data.description.trim(),
              duration: data.duration,
              isExam: homework.isExam,
              format,
              roomId: format === 'offline' ? data.roomId : undefined,
              conferenceLink: format === 'meeting' ? data.conferenceLink.trim() : undefined,
              scheduledAt,
              endDate: endDate || undefined,
              groupId,
              isOnlyVideoLesson: isOnlyVideo,
              lessonFiles: isOnlyVideo && sessionFiles.length > 0 ? sessionFiles : undefined,
              ...(hasAssignment
                ? {
                  assignmentText: homework.assignmentText,
                  assignmentDescription: homework.assignmentDescription,
                  assignmentAttempts: homework.numberOfAttempts || 1,
                  assignmentFiles: homeworkFiles.length > 0 ? homeworkFiles : undefined,
                }
                : {}),
            },
          }).unwrap();
          lessonId = result.data.lesson.id;
          setCreatedLessonId(lessonId);
        }

        const { queuedVideos, queuedPackages } = await saveSteps(
          lessonId,
          steps,
          markStep,
          data.title.trim(),
        );
        const backgroundUploads = describeBackgroundUploads(queuedVideos, queuedPackages);

        toast({
          title: 'Success',
          description: backgroundUploads
            ? `Session created. ${backgroundUploads} uploading in the background`
            : 'Session created successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error) {
        const lessonCreated = lessonId !== null;
        toast({
          title: lessonCreated ? 'Session created, but not all steps were saved' : 'Error',
          description: lessonCreated
            ? `${getSaveErrorMessage(error, 'Failed to save steps')}. Press "Save steps" to try again`
            : getSaveErrorMessage(error, 'Failed to create session'),
          color: 'danger',
        });
      }
    };

    const submitSession = handleSessionSubmit(onSubmit, () => setActiveTab('session'));

    const watchedSessionTitle = watchSession('title');
    const watchedSessionDescription = watchSession('description');
    const watchedSessionStartTime = watchSession('startTime');
    const watchedSessionDuration = watchSession('duration');
    const canSubmitSession =
      !!watchedSessionTitle?.trim() &&
      !!watchedSessionDescription?.trim() &&
      !!watchedSessionDate &&
      !!watchedSessionStartTime &&
      !!watchedSessionDuration &&
      (watchedSessionFormat !== 'offline' || !!watchSession('roomId')) &&
      (watchedSessionFormat !== 'meeting' || !!watchSession('conferenceLink')?.trim()) &&
      steps.length > 0;

    useEffect(() => {
      if (watchedSessionFormat !== 'offline') {
        setSessionValue('locationId', undefined);
        setSessionValue('roomId', undefined);
        clearSessionErrors(['locationId', 'roomId']);
      }
      if (watchedSessionFormat !== 'meeting') {
        setSessionValue('conferenceLink', '');
        clearSessionErrors('conferenceLink');
      }
    }, [watchedSessionFormat, setSessionValue, clearSessionErrors]);

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        backdrop="opaque"
        scrollBehavior="inside"
        isDismissable={!isBusy}
        isKeyboardDismissDisabled={isBusy}
        hideCloseButton={isBusy}
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
          wrapper: styles.modalContent,
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className={styles.title}>
                {activeTab === 'session' ? 'Add new session' : 'Assignments'}
              </ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Tabs
                  aria-label="Session type tabs"
                  selectedKey={activeTab}
                  onSelectionChange={(key) => setActiveTab(key as string)}
                  classNames={{
                    tabList: 'gap-0 bg-transparent w-fit',
                    tab: 'px-3 py-2 bg-transparent text-grey-secondary data-[selected=true]:bg-[#1E1E1E] rounded-lg',
                    tabContent: 'text-xs text-grey-secondary data-[selected=true]:text-[#FFFFFF]',
                  }}
                >
                  <Tab key="session" title="Add new session">
                    <form
                      onSubmit={submitSession}
                      className="flex flex-col gap-6"
                      id="session-form"
                      noValidate
                    >
                      <Controller
                        name="format"
                        control={sessionControl}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value}
                            onValueChange={(value) => {
                              if (value === 'offline' && steps.length > 1) {
                                setOfflineSwitchOpen(true);
                                return;
                              }
                              field.onChange(value as LessonFormat);
                            }}
                            classNames={{ wrapper: 'flex flex-row gap-6' }}
                          >
                            <Radio
                              value="online"
                              classNames={{ label: 'text-sm !text-black-headline' }}
                            >
                              Online session
                            </Radio>
                            <Radio
                              value="offline"
                              classNames={{ label: 'text-sm !text-black-headline' }}
                            >
                              Offline session
                            </Radio>
                            <Radio
                              value="meeting"
                              classNames={{ label: 'text-sm !text-black-headline' }}
                            >
                              Meeting
                            </Radio>
                          </RadioGroup>
                        )}
                      />
                      {watchedSessionFormat === 'online' && (
                        <Controller
                          name="isOnlyVideo"
                          control={sessionControl}
                          render={({ field }) => (
                            <Checkbox
                              isSelected={field.value}
                              onValueChange={field.onChange}
                              classNames={{
                                base: 'w-full',
                                label: 'text-sm !text-black-headline',
                              }}
                            >
                              Only video
                            </Checkbox>
                          )}
                        />
                      )}

                      <Controller
                        name="title"
                        control={sessionControl}
                        render={({ field }) => (
                          <CustomInput
                            {...field}
                            label="Session Title"
                            labelPlacement="outside"
                            isRequired
                            placeholder="Enter session title"
                            errorMessage={sessionErrors.title?.message}
                            showError={!!sessionErrors.title}
                          />
                        )}
                      />

                      <Controller
                        name="description"
                        control={sessionControl}
                        render={({ field }) => (
                          <CustomInput
                            {...field}
                            label="Session Description"
                            labelPlacement="outside"
                            isRequired
                            placeholder="Enter session description"
                            errorMessage={sessionErrors.description?.message}
                            showError={!!sessionErrors.description}
                          />
                        )}
                      />

                      <div className="flex flex-col gap-2">
                        <Controller
                          name="date"
                          control={sessionControl}
                          render={({ field }) => (
                            <CustomDatepicker
                              label="Date"
                              labelPlacement="outside"
                              isRequired
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                revalidateStartTime();
                              }}
                              onBlur={field.onBlur}
                              errorMessage={sessionErrors.date?.message}
                              showError={!!sessionErrors.date}
                              {...sessionDatePickerProps}
                              placeholderValue={null}
                            />
                          )}
                        />
                        <FieldHint>{formatSessionDateHint(termBounds)}</FieldHint>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-full">
                          <Controller
                            name="startTime"
                            control={sessionControl}
                            render={({ field }) => (
                              <CustomTimeInput
                                label="Start time"
                                labelPlacement="outside"
                                isRequired
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                errorMessage={sessionErrors.startTime?.message}
                                showError={!!sessionErrors.startTime}
                              />
                            )}
                          />
                          {!sessionErrors.startTime && (
                            <p className="text-xs text-grey-secondary mt-1">
                              Time is shown in your local time zone
                            </p>
                          )}
                        </div>
                        <div className="w-full">
                          <Controller
                            name="duration"
                            control={sessionControl}
                            render={({ field }) => (
                              <DurationField
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  revalidateStartTime();
                                }}
                                onBlur={field.onBlur}
                                errorMessage={sessionErrors.duration?.message}
                                isRequired
                              />
                            )}
                          />
                        </div>
                      </div>
                      {watchedSessionFormat === 'offline' && (
                        <div className="flex gap-3">
                          <div className="w-full">
                            <Controller
                              name="locationId"
                              control={sessionControl}
                              render={({ field }) => (
                                <CustomSelect
                                  label="Campus"
                                  labelPlacement="outside"
                                  isRequired
                                  placeholder="Enter Campus"
                                  style="black"
                                  items={locationItems}
                                  selectedKeys={
                                    field.value ? new Set([String(field.value)]) : new Set()
                                  }
                                  onSelectionChange={(keys) => {
                                    const value = Array.from(keys as Set<string>)[0];
                                    field.onChange(value ? Number(value) : undefined);
                                    setSessionValue('roomId', undefined);
                                  }}
                                  onClose={field.onBlur}
                                  errorMessage={sessionErrors.locationId?.message}
                                  showError={!!sessionErrors.locationId}
                                />
                              )}
                            />
                          </div>
                          <div className="w-full">
                            <Controller
                              name="roomId"
                              control={sessionControl}
                              render={({ field }) => (
                                <CustomSelect
                                  label="Room"
                                  labelPlacement="outside"
                                  isRequired
                                  placeholder="Enter Room"
                                  style="black"
                                  items={roomItems}
                                  selectedKeys={
                                    field.value ? new Set([String(field.value)]) : new Set()
                                  }
                                  onSelectionChange={(keys) => {
                                    const value = Array.from(keys as Set<string>)[0];
                                    field.onChange(value ? Number(value) : undefined);
                                  }}
                                  onClose={field.onBlur}
                                  errorMessage={sessionErrors.roomId?.message}
                                  showError={!!sessionErrors.roomId}
                                />
                              )}
                            />
                          </div>
                        </div>
                      )}
                      {watchedSessionFormat === 'meeting' && (
                        <Controller
                          name="conferenceLink"
                          control={sessionControl}
                          render={({ field }) => (
                            <CustomInput
                              {...field}
                              label="Conference link"
                              labelPlacement="outside"
                              isRequired
                              placeholder="https://meet.google.com/..."
                              errorMessage={sessionErrors.conferenceLink?.message}
                              showError={!!sessionErrors.conferenceLink}
                            />
                          )}
                        />
                      )}
                      {watchedSessionFormat === 'online' && watchSession('isOnlyVideo') && (
                        <Dropzone
                          files={sessionFiles}
                          setFiles={setSessionFiles}
                          multiple
                          accept={SESSION_ATTACHMENT_ACCEPT}
                          withPreviewList
                          maxSize={MAX_FILE_SIZE}
                          className="p-8"
                        >
                          <div className="flex items-center justify-center gap-6">
                            <Svg
                              Icon={UploadVideoIcon}
                              width={40}
                              height={40}
                              className="text-red"
                            />
                            <div className="flex flex-col gap-y-1">
                              <p className="text-black font-bold text-sm">
                                Upload or Drag and Drop your File
                              </p>
                              <p className="text-grey-description text-xs">
                                {MAX_FILE_SIZE_HINT}
                              </p>
                              <p className="text-grey-description text-xs">
                                {SESSION_ATTACHMENT_TYPES_HINT}
                              </p>
                            </div>
                          </div>
                        </Dropzone>
                      )}
                      {steps.length > 0 && <Divider />}
                      <SessionStepsField
                        steps={steps}
                        onChange={setSteps}
                        maxSteps={watchedSessionFormat === 'offline' ? 1 : SESSION_STEP_LIMIT}
                        // Steps are added only after the session fields are valid
                        onBeforeAdd={() => triggerSession(undefined, { shouldFocus: true })}
                      />
                      {steps.length === 0 && (
                        <FieldHint>Add at least one step to create the session</FieldHint>
                      )}
                    </form>
                  </Tab>

                  <Tab key="homework" title="Assignments">
                    <div className="flex flex-col gap-6">
                      <div className="flex gap-4">
                        {/* Formative = isExam: false */}
                        <Controller
                          name="isExam"
                          control={homeworkControl}
                          render={({ field }) => (
                            <Checkbox
                              isSelected={field.value === false}
                              onValueChange={(val) => {
                                if (val) {
                                  field.onChange(false);
                                }
                              }}
                              classNames={{
                                base: 'w-full',
                                label: 'text-sm !text-black-headline',
                              }}
                            >
                              Formative
                            </Checkbox>
                          )}
                        />
                        {/* Summative = isExam: true */}
                        <Controller
                          name="isExam"
                          control={homeworkControl}
                          render={({ field }) => (
                            <Checkbox
                              isSelected={field.value === true}
                              onValueChange={(val) => {
                                if (val) {
                                  field.onChange(true);
                                }
                              }}
                              classNames={{
                                base: 'w-full',
                                label: 'text-sm !text-black-headline',
                              }}
                            >
                              Summative
                            </Checkbox>
                          )}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <Controller
                          name="deadline"
                          control={homeworkControl}
                          render={({ field }) => (
                            <CustomDatepicker
                              label="Deadline"
                              labelPlacement="outside"
                              value={field.value}
                              onChange={field.onChange}
                              errorMessage={homeworkErrors.deadline?.message}
                              showError={!!homeworkErrors.deadline}
                              {...deadlinePickerProps}
                              placeholderValue={null}
                            />
                          )}
                        />
                        <FieldHint>{deadlineHint}</FieldHint>
                      </div>

                      <Controller
                        name="numberOfAttempts"
                        control={homeworkControl}
                        render={({ field }) => (
                          <CustomInput
                            {...field}
                            value={field.value?.toString() || ''}
                            onChange={(e) => field.onChange(Number(e.target.value) || 1)}
                            type="number"
                            label="Number of attempts"
                            labelPlacement="outside"
                            placeholder="Enter number of attempts"
                            errorMessage={homeworkErrors.numberOfAttempts?.message}
                            showError={!!homeworkErrors.numberOfAttempts}
                          />
                        )}
                      />

                      <Controller
                        name="assignmentText"
                        control={homeworkControl}
                        render={({ field }) => (
                          <CustomInput
                            {...field}
                            label="Assignment"
                            labelPlacement="outside"
                            placeholder="Enter homework assignment"
                            errorMessage={homeworkErrors.assignmentText?.message}
                            showError={!!homeworkErrors.assignmentText}
                          />
                        )}
                      />

                      <Controller
                        name="assignmentDescription"
                        control={homeworkControl}
                        render={({ field }) => (
                          <CustomTextarea
                            {...field}
                            label="Assignment Description"
                            labelPlacement="outside"
                            placeholder="Enter homework description"
                            errorMessage={homeworkErrors.assignmentDescription?.message}
                            showError={!!homeworkErrors.assignmentDescription}
                          />
                        )}
                      />

                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-black-headline">
                          Upload files
                        </label>
                        <Dropzone
                          files={homeworkFiles}
                          setFiles={setHomeworkFiles}
                          multiple
                          accept={SESSION_ATTACHMENT_ACCEPT}
                          withPreviewList
                          className="p-12"
                        >
                          <div className="flex items-center justify-center gap-6 ">
                            <Svg
                              Icon={UploadVideoIcon}
                              width={48}
                              height={48}
                              className="text-red"
                            />
                            <div className="flex flex-col gap-y-2">
                              <p className="text-black font-bold leading-[1] tracking-[-5%]">
                                Upload or Drag and Drop your File
                              </p>
                              <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                {MAX_FILE_SIZE_HINT}
                              </p>
                              <p className="text-grey-description text-sm leading-[1] tracking-[-5%]">
                                {SESSION_ATTACHMENT_TYPES_HINT}
                              </p>
                            </div>
                          </div>
                        </Dropzone>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </ModalBody>
              <ModalFooter className="flex-col gap-3 pt-6">
                {saveStatus && (
                  <p className="text-sm text-grey-secondary" role="status">
                    {saveStatus}
                  </p>
                )}
                <div className="flex w-full gap-4">
                  <AppButton
                    fullWidth
                    color="white"
                    onPress={handleClose}
                    type="button"
                    disabled={isBusy}
                  >
                    Cancel
                  </AppButton>
                  <AppButton
                    fullWidth
                    color="black"
                    type="button"
                    isLoading={isBusy}
                    disabled={!canSubmitSession || isBusy}
                    onPress={submitSession}
                  >
                    {createdLessonId ? 'Save steps' : 'Create session'}
                  </AppButton>
                </div>
              </ModalFooter>
              <MessageModal
                isOpen={offlineSwitchOpen}
                onOpenChange={() => setOfflineSwitchOpen(false)}
                title="Switch to Offline?"
                text={offlineSwitchText(steps.length)}
                confirmText="Switch and delete steps"
                variant="black"
                toggleVisibility={() => {
                  setSteps((current) => current.slice(0, 1));
                  setSessionValue('format', 'offline');
                  setOfflineSwitchOpen(false);
                }}
              />
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

AddSessionModal.displayName = 'AddSessionModal';
